import { useRef, useEffect, type FC } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import { interpolateColor } from "@tennr/frontend/utils/interpolateColor";
import "./AnimatedLogo.css";
import { MotionValue } from "framer-motion";

interface FillOptions {
  fillColor?: string;
  isLightBackground?: boolean;
  dynamicFill: MotionValue<string>;
  enableScrollEffects: boolean;
  animatedFill: MotionValue<string>;
  startColor: string;
}

function getFinalFill({
  fillColor,
  isLightBackground,
  dynamicFill,
  enableScrollEffects,
  animatedFill,
  startColor,
}: FillOptions): string | MotionValue<string> {
  if (fillColor !== undefined) return fillColor;
  if (isLightBackground !== undefined) return dynamicFill;
  if (enableScrollEffects) return animatedFill;
  return startColor;
}

export interface AnimatedLogoProps {
  // base dimensions
  width?: number | string;
  height?: number | string;

  // colors - can be static or scroll-based
  fillColor?: string;
  startColor?: string;
  endColor?: string;
  // dynamic fill override
  isLightBackground?: boolean;

  // opacity - can be static or scroll-based
  opacity?: number;
  startOpacity?: number;
  endOpacity?: number;

  // scale - for performance, we scale instead of changing width/height
  scale?: number;
  startScale?: number;
  endScale?: number;

  // scroll behavior
  enableScrollEffects?: boolean;
  scrollOffset?: [
    "start end" | "end start" | "start center" | "center start" | "center end" | "end center",
    "start end" | "end start" | "start center" | "center start" | "center end" | "end center",
  ]; // e.g., ["start end", "end start"]

  // parallax
  enableParallax?: boolean;
  parallaxAmount?: number;

  // entry animation
  fadeInDelay?: number;
  fadeInDuration?: number;

  className?: string;
}

export const AnimatedLogo: FC<AnimatedLogoProps> = ({
  // dimensions
  width = 200,
  height = "auto",

  // colors
  fillColor,
  startColor = "#EEDFD1",
  endColor = "#AE5249",
  isLightBackground,

  // opacity
  opacity,
  startOpacity = 1,
  endOpacity = 1,

  // scale
  scale,
  startScale = 1,
  endScale = 1,

  // scroll behavior
  enableScrollEffects = true,
  scrollOffset = ["start end", "end start"],

  // parallax
  enableParallax = false,
  parallaxAmount = 50,

  className = "",
}) => {
  const logoRef = useRef<HTMLDivElement>(null);

  // scroll-based animations
  const { scrollYProgress } = useScroll({
    target: logoRef,
    offset: scrollOffset,
  });

  // parallax effect
  const y = useTransform(scrollYProgress, [0, 1], enableParallax ? [-parallaxAmount, parallaxAmount] : [0, 0]);

  // scale - use static value if provided, otherwise animate based on scroll
  const animatedScale = useTransform(scrollYProgress, [0, 0.5, 1], [startScale, (startScale + endScale) / 2, endScale]);
  const finalScale = scale !== undefined ? scale : enableScrollEffects ? animatedScale : startScale;

  // opacity - use static value if provided, otherwise animate based on scroll
  const animatedOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [startOpacity, 1, 1, endOpacity]);
  const finalOpacity = opacity !== undefined ? opacity : enableScrollEffects ? animatedOpacity : startOpacity;

  const dynamicFill = useMotionValue(
    isLightBackground === undefined ? startColor : isLightBackground ? "#5F4037" : "#EEDFD1"
  );

  // Update on isLightBackground change
  useEffect(() => {
    if (isLightBackground !== undefined) {
      dynamicFill.set(isLightBackground ? "#5F4037" : "#EEDFD1");
    }
  }, [isLightBackground, dynamicFill]);

  // Scroll-based animated fill
  const animatedFill = useTransform(scrollYProgress, [0, 1], [startColor, endColor], {
    mixer: (from, to) => (p) => interpolateColor(from as string, to as string, p),
  });

  const finalFill = getFinalFill({
    fillColor,
    isLightBackground,
    dynamicFill,
    enableScrollEffects,
    animatedFill,
    startColor,
  });

  // entry animation variants
  // const logoVariants = {
  //   hidden: {
  //     opacity: 0,
  //     scale: 0.9,
  //     y: 20,
  //   },
  //   visible: {
  //     opacity: 1,
  //     scale: 1,
  //     y: 0,
  //     transition: {
  //       delay: fadeInDelay,
  //       duration: fadeInDuration,
  //       ease: [0.21, 0.47, 0.32, 0.98],
  //     },
  //   },
  //   exit: {
  //     opacity: 0,
  //     scale: 0.95,
  //     transition: {
  //       duration: 0.3,
  //     },
  //   },
  // };

  return (
    <motion.div
      ref={logoRef}
      className={`animated-logo ${className}`}
      whileHover={{ opacity: 1 }}
      style={{
        y: enableParallax ? y : 0,
        scale: finalScale,
        opacity: finalOpacity,
        width,
        height,
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 580 148"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <motion.path
          d="M526.555 41.6433H541.469V80.1719L538.155 78.5147C541.193 69.1243 544.3 61.6671 547.476 56.1433C550.652 50.4814 554.45 46.2695 558.869 43.5076C563.426 40.6076 568.95 39.1576 575.44 39.1576C576.821 39.1576 577.857 39.2266 578.548 39.3647V56.1433C577.305 55.8671 575.855 55.729 574.198 55.729C564.117 55.729 556.176 59.5266 550.376 67.1219C544.576 74.579 541.676 84.4528 541.676 96.7433V145.008H526.555V41.6433Z"
          style={{ fill: finalFill }}
        />
        <motion.path
          d="M427.849 41.6239H442.97V71.2453L440.277 67.1025C445.111 58.1263 450.772 51.2215 457.263 46.3882C463.891 41.4167 471.487 38.931 480.049 38.931C486.539 38.931 492.201 40.4501 497.034 43.4882C502.006 46.5263 505.803 50.9453 508.427 56.7453C511.189 62.5453 512.57 69.4501 512.57 77.4596V144.988H497.241V83.2596C497.241 72.212 495.377 64.4096 491.649 59.8525C488.058 55.1572 482.534 52.8096 475.077 52.8096C469.415 52.8096 464.099 54.3977 459.127 57.5739C454.294 60.7501 450.358 65.3072 447.32 71.2453C444.42 77.0453 442.97 83.7429 442.97 91.3382V144.988H427.849V41.6239Z"
          style={{ fill: finalFill }}
        />
        <motion.path
          d="M330.729 41.6239H345.85V71.2453L343.157 67.1025C347.99 58.1263 353.652 51.2215 360.143 46.3882C366.771 41.4167 374.367 38.931 382.929 38.931C389.419 38.931 395.081 40.4501 399.914 43.4882C404.886 46.5263 408.683 50.9453 411.307 56.7453C414.069 62.5453 415.45 69.4501 415.45 77.4596V144.988H400.121V83.2596C400.121 72.212 398.257 64.4096 394.529 59.8525C390.938 55.1572 385.414 52.8096 377.957 52.8096C372.295 52.8096 366.979 54.3977 362.007 57.5739C357.174 60.7501 353.238 65.3072 350.2 71.2453C347.3 77.0453 345.85 83.7429 345.85 91.3382V144.988H330.729V41.6239Z"
          style={{ fill: finalFill }}
        />
        <motion.path
          d="M275.32 147.474C265.654 147.474 257.092 145.264 249.635 140.845C242.316 136.288 236.585 129.936 232.442 121.788C228.437 113.641 226.435 104.181 226.435 93.4096C226.435 82.7763 228.437 73.3167 232.442 65.031C236.585 56.7453 242.246 50.3239 249.427 45.7667C256.608 41.2096 264.894 38.931 274.285 38.931C282.708 38.931 290.235 41.0025 296.863 45.1453C303.492 49.1501 308.739 54.881 312.606 62.3382C316.473 69.7953 318.406 78.4953 318.406 88.4382C318.406 89.4048 318.406 90.1644 318.406 90.7167C318.406 91.131 318.406 91.8906 318.406 92.9953H235.549V80.1525H311.57L302.249 87.4025C302.249 85.7453 302.249 84.0882 302.249 82.431C302.249 73.1786 299.556 65.7906 294.17 60.2667C288.923 54.7429 282.156 51.981 273.87 51.981C268.07 51.981 262.754 53.431 257.92 56.331C253.225 59.231 249.496 63.4429 246.735 68.9667C243.973 74.4906 242.523 80.7739 242.385 87.8167C242.246 88.9215 242.177 89.681 242.177 90.0953C242.177 90.5096 242.108 91.2001 241.97 92.1667C241.832 105.148 244.801 115.436 250.877 123.031C256.954 130.488 265.101 134.217 275.32 134.217C282.087 134.217 287.749 132.491 292.306 129.038C297.001 125.586 300.316 120.683 302.249 114.331H317.577C314.954 124.136 309.982 132.145 302.663 138.36C295.482 144.436 286.368 147.474 275.32 147.474Z"
          style={{ fill: finalFill }}
        />
        <motion.path
          d="M191.794 14.2929H135.658V0H263.88V14.2929H207.744V145H191.794V14.2929Z"
          style={{ fill: finalFill }}
        />
        <motion.path
          d="M79.7178 42.4619H135.642V144.997H56.4092V109.788H79.7129V102.538H56.4092V102.536H0.486328V0H79.7178V42.4619Z"
          style={{ fill: finalFill }}
        />
      </svg>
    </motion.div>
  );
};
